/**
 * CR130 Phase 3: Side Panel Mode (Fixed-Width Inspector)
 * CR304: Uses centralized display helpers for title/condition
 * 
 * Fixed-width inspector panel (400px) that shows deal details.
 * Non-modal: sits alongside cards, doesn't block interaction with the list.
 * Stays open while browsing; selecting another card swaps content.
 * 
 * CR130 Review Fix: Changed from Sheet (modal overlay) to fixed div rail.
 */

import { useEffect, useCallback, useRef } from "react";
import { X, ExternalLink, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DealDetailsContent } from "./DealDetailsContent";
import { DealTypeBadge } from "./DealTypeBadge";
import { DealStatusBadge, computeDealStatus } from "./DealStatusBadge";
import type { Deal } from "@/lib/types/deals";
import type { DetailsSectionConfig } from "@/lib/layout-studio/dealCardUXTypes";
import { getDealType } from "@/lib/deals/getDealType";
import { getHeadlineMechanic } from "@/lib/types/deals";
import { getDealCardTitle, getDealCardCondition } from "@/lib/deals/dealCardDisplay";
import { cn } from "@/lib/utils";

// ================================================================
// PANEL COMPONENT (fixed-width right rail, non-modal)
// ================================================================

interface DealDetailsPanelProps {
  deal: (Deal & { id?: string }) | null;
  sectionsConfig: DetailsSectionConfig[];
  isAdmin?: boolean;
  isOpen: boolean;
  onClose: () => void;
  /** Width of the panel */
  width?: number;
}

export function DealDetailsPanel({
  deal,
  sectionsConfig,
  isAdmin = false,
  isOpen,
  onClose,
  width = 400,
}: DealDetailsPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  
  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);
  
  // Don't render anything if closed or no deal
  if (!isOpen || !deal) return null;
  
  const { category: dealCategory } = getDealType(deal);
  // CR432: Use unified helper via deal object
  const dealStatus = computeDealStatus(deal as any);
  const headline = getHeadlineMechanic(deal);
  
  return (
    <div
      ref={panelRef}
      className={cn(
        "fixed top-0 right-0 h-screen bg-background border-l border-border shadow-lg z-40",
        "flex flex-col",
        "transition-transform duration-200 ease-out",
      )}
      style={{ width: `${width}px` }}
      role="complementary"
      aria-label="Deal details panel"
    >
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b bg-muted/30 shrink-0">
        <div className="flex-1 min-w-0 pr-2">
          <h2 className="text-lg font-semibold truncate">
            {getDealCardTitle(deal)}
          </h2>
          <p className="text-sm text-muted-foreground truncate mt-0.5">
            {getDealCardCondition(deal) || headline}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="h-8 w-8 p-0 shrink-0"
          aria-label="Close panel"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Badges row */}
      <div className="flex flex-wrap gap-1.5 px-4 py-2 border-b bg-muted/10 shrink-0">
        <DealTypeBadge category={dealCategory} size="sm" />
        <DealStatusBadge status={dealStatus} />
        {deal.reward_brand_name && deal.reward_brand_name !== deal.promo_merchant_name && (
          <Badge variant="secondary" className="text-xs">
            {deal.reward_brand_name}
          </Badge>
        )}
      </div>
      
      {/* Scrollable content - using native scroll for reliability */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <DealDetailsContent
          deal={deal}
          sectionsConfig={sectionsConfig}
          mode="panel"
          isAdmin={isAdmin}
        />
      </div>
      
      {/* Footer with primary action */}
      {deal.proof_urls?.[0] && (
        <div className="p-4 border-t bg-muted/30 shrink-0">
          <Button asChild className="w-full">
            <a href={deal.proof_urls[0]} target="_blank" rel="noopener noreferrer">
              View Deal <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}

// ================================================================
// TRIGGER BUTTON (to be placed on cards)
// ================================================================

interface DealDetailsPanelTriggerProps {
  onClick: () => void;
  isSelected?: boolean;
  className?: string;
}

export function DealDetailsPanelTrigger({
  onClick,
  isSelected = false,
  className,
}: DealDetailsPanelTriggerProps) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "px-3 py-1.5 text-xs font-medium rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        isSelected 
          ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2" 
          : "bg-primary text-primary-foreground hover:bg-primary/90",
        className
      )}
      aria-label="Show deal details"
    >
      <span className="flex items-center gap-1">
        Details
        <ChevronRight className="w-3 h-3" />
      </span>
    </button>
  );
}

export default DealDetailsPanel;
